import { Popover } from './atoms/Popover';
import { Button } from 'foundations-components/transitional/components/Button';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Select } from 'foundations-components/transitional/components/Select';
import { useCallback, useMemo, useState } from 'react';
import { usePortalData } from '../data/usePortalData';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UISortableList from 'ui-addon-draggable/components/connected/UISortableList';
import { DragHandle } from 'foundations-components/transitional/components/DragHandle';
import { DataSyncIcon } from 'foundations-assets/icons/components/DataSyncIcon';
import styled from 'foundations-theming-specialty/utils';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { MobilePopover } from './MobilePopover';
import { useIsMobile } from '../hooks/useBreakpoint';
import { css } from 'styled-components';
import { usageTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Rotate = styled.div.withConfig({
  displayName: "TicketListingsSortPopover__Rotate"
})(["transform:rotate(", "deg);"], ({
  $degrees
}) => $degrees);
const SelectsGrid = styled.div.withConfig({
  displayName: "TicketListingsSortPopover__SelectsGrid"
})(["display:flex;overflow:hidden;flex:1 1 100%;gap:", ";", " > *{overflow:hidden;flex:1 1 0px;", "}"], ({
  theme
}) => theme.space[300], ({
  $isMobile
}) => $isMobile && css(["flex-direction:column;"]), ({
  $isMobile
}) => $isMobile && css(["flex:0 0 auto;margin:0;"]));
const SortPopoverContent = ({
  sortingList,
  setSort,
  sort,
  updateRow,
  addRow,
  removeAt,
  propertyOptions,
  resetSort
}) => {
  const isMobile = useIsMobile();
  const used = new Set(sortingList.map(s => s.property));
  const isOnlyDefaultCreatedDesc = sort.length === 1 && sortingList.length === 1 && sortingList[0].property === 'createdate' && sortingList[0].direction === 'descending';
  const items = sortingList.map((item, index) => ({
    id: item.property,
    ariaLabel: item.label || item.property,
    children: /*#__PURE__*/_jsxs(Flex, {
      gap: "600",
      paddingBlock: "200",
      alignItems: "center",
      inlineSize: "100%",
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(DragHandle, {
          position: "start",
          size: "default",
          use: "default",
          marginInlineEnd: "-400",
          children: /*#__PURE__*/_jsx("div", {
            style: {
              width: 16,
              height: 16
            }
          })
        })
      }), /*#__PURE__*/_jsxs(SelectsGrid, {
        $isMobile: isMobile,
        children: [/*#__PURE__*/_jsx(Select, {
          searchable: false,
          value: item.property,
          onChange: e => {
            usageTracker.track('Customer Portal App', 'Tickets Discovered', {
              action: 'tickets-sorted',
              screen: 'ticket-listings',
              sortField: e.target.value
            });
            updateRow(index, e.target.value, item.direction);
          },
          options: propertyOptions.filter(o => !used.has(o.value) || o.value === item.property)
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Select, {
            searchable: false,
            value: item.direction,
            onChange: e => updateRow(index, item.property, e.target.value),
            options: [{
              text: I18n.text('customer-portal-app.sort.ascending'),
              value: 'ascending'
            }, {
              text: I18n.text('customer-portal-app.sort.descending'),
              value: 'descending'
            }]
          })
        })]
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          disabled: isOnlyDefaultCreatedDesc,
          onClick: () => {
            if (isOnlyDefaultCreatedDesc) {
              return;
            }
            removeAt(index);
          },
          children: /*#__PURE__*/_jsx(DeleteIcon, {
            size: "300",
            color: isOnlyDefaultCreatedDesc ? 'icon-core-disabled' : 'icon-core-subtle'
          })
        })
      })]
    })
  }));
  const onOrderChange = useCallback(newItems => {
    const newOrderIds = Array.isArray(newItems) ? typeof newItems[0] === 'string' ? newItems : newItems.map(i => i.id) : [];
    const propertyToToken = {};
    sortingList.forEach(s => {
      propertyToToken[s.property] = s.direction === 'descending' ? `-${s.property}` : s.property;
    });
    setSort(newOrderIds.map(p => propertyToToken[p]).filter(Boolean));
  }, [setSort, sortingList]);
  return /*#__PURE__*/_jsxs(Flex, {
    maxInlineSize: isMobile ? '100%' : '650px',
    inlineSize: isMobile ? '100%' : '500px',
    flexDirection: "column",
    gap: "400",
    paddingInline: "400",
    paddingBlock: "1000",
    children: [!isMobile && /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-100",
        paddingInline: "600",
        color: "text-core-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.sortLabel"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        size: "small",
        onClick: () => {
          usageTracker.track('Customer Portal App', 'Tickets Discovered', {
            action: 'tickets-sort-cleared',
            screen: 'ticket-listings'
          });
          resetSort();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.sort.resetSort"
        })
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      children: /*#__PURE__*/_jsx(UISortableList, {
        items: items,
        onOrderChange: onOrderChange
      })
    }), /*#__PURE__*/_jsxs(Button, {
      paddingInline: "600",
      paddingBlock: "0",
      size: "small",
      use: "transparent",
      onClick: addRow,
      children: [/*#__PURE__*/_jsx(AddIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.sort.addSortColumn"
      })]
    })]
  });
};
const TicketListingsSortButton = ({
  sortCount,
  setOpen
}) => /*#__PURE__*/_jsxs(Button, {
  margin: "0",
  use: "transparent",
  onClick: () => {
    setOpen(prev => {
      if (!prev) {
        usageTracker.track('Customer Portal App', 'Tickets Discovered', {
          action: 'ticket-sort-discovered',
          screen: 'ticket-listings'
        });
      }
      return !prev;
    });
  },
  children: [/*#__PURE__*/_jsx(Rotate, {
    $degrees: 90,
    children: /*#__PURE__*/_jsx(DataSyncIcon, {})
  }), /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.sortLabel"
    }), sortCount > 0 && ` (${sortCount})`]
  })]
});
export const TicketListingsSortPopover = ({
  sort,
  setSort,
  resetSort
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const {
    data
  } = usePortalData();
  const sortingList = useMemo(() => sort.map(s => {
    var _data$configuredTicke;
    const isDesc = s.startsWith('-');
    const prop = isDesc ? s.slice(1) : s;
    const label = (data === null || data === void 0 || (_data$configuredTicke = data.configuredTicketProperties.find(c => c.propertyName === prop)) === null || _data$configuredTicke === void 0 ? void 0 : _data$configuredTicke.displayNameOverride) || prop;
    return {
      property: prop,
      direction: isDesc ? 'descending' : 'ascending',
      label
    };
  }), [sort, data === null || data === void 0 ? void 0 : data.configuredTicketProperties]);
  const propertyOptions = useMemo(() => {
    const options = ((data === null || data === void 0 ? void 0 : data.configuredTicketProperties) || []).map(p => ({
      text: p.displayNameOverride || p.propertyName,
      value: p.propertyName
    }));
    if (sortingList.some(s => s.property === 'createdate') && !options.some(o => o.value === 'createdate')) {
      options.unshift({
        text: I18n.text('customer-portal-app.property.createdate'),
        value: 'createdate'
      });
    }
    return options;
  }, [data === null || data === void 0 ? void 0 : data.configuredTicketProperties, sortingList]);
  const updateRow = useCallback((index, nextProperty, nextDirection) => {
    const token = nextDirection === 'descending' ? `-${nextProperty}` : nextProperty;
    const next = [...sort];
    next[index] = token;
    setSort(next);
  }, [setSort, sort]);
  const addRow = useCallback(() => {
    var _propertyOptions$find;
    const used = new Set(sortingList.map(s => s.property));
    const firstUnused = (_propertyOptions$find = propertyOptions.find(o => !used.has(o.value))) === null || _propertyOptions$find === void 0 ? void 0 : _propertyOptions$find.value;
    if (!firstUnused) return;
    setSort([...sort, `-${firstUnused}`]);
  }, [setSort, sort, propertyOptions, sortingList]);
  const removeAt = useCallback(index => {
    const next = [...sort];
    next.splice(index, 1);
    setSort(next);
  }, [setSort, sort]);
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobilePopover, {
      title: I18n.text('customer-portal-app.sortLabel'),
      open: open,
      onOpenChange: setOpen,
      content: /*#__PURE__*/_jsx(SortPopoverContent, {
        sortingList: sortingList,
        setSort: setSort,
        sort: sort,
        updateRow: updateRow,
        addRow: addRow,
        removeAt: removeAt,
        propertyOptions: propertyOptions,
        resetSort: resetSort
      }),
      footer: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "start",
        gap: "400",
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "primary",
            onClick: () => setOpen(false),
            children: /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customer-portal-app.show"
              })
            })
          })
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "transparent",
            onClick: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-sort-cleared',
                screen: 'ticket-listings'
              });
              resetSort();
            },
            children: /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customer-portal-app.sort.resetSort"
              })
            })
          })
        })]
      }),
      children: /*#__PURE__*/_jsx(TicketListingsSortButton, {
        sortCount: sort.length,
        open: open,
        setOpen: setOpen
      })
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    open: open,
    autoPlacement: false,
    placement: "bottom left",
    onOpenChange: e => setOpen(e.currentTarget.value),
    content: /*#__PURE__*/_jsx(SortPopoverContent, {
      sortingList: sortingList,
      setSort: setSort,
      sort: sort,
      updateRow: updateRow,
      addRow: addRow,
      removeAt: removeAt,
      propertyOptions: propertyOptions,
      resetSort: resetSort
    }),
    children: /*#__PURE__*/_jsx(TicketListingsSortButton, {
      sortCount: sort.length,
      open: open,
      setOpen: setOpen
    })
  });
};